import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { HiCheck, HiExclamation, HiInformationCircle, HiX } from 'react-icons/hi';
import ModalOverlay from '../components/ModalOverlay';

const NotificationContext = createContext(null);

let toastId = 0;

const TYPE_STYLES = {
  success: {
    bg: 'bg-green-50 border-green-200 text-green-800',
    icon: HiCheck,
    iconColor: 'text-green-600',
  },
  error: {
    bg: 'bg-red-50 border-red-200 text-red-800',
    icon: HiX,
    iconColor: 'text-red-600',
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200 text-amber-900',
    icon: HiExclamation,
    iconColor: 'text-amber-600',
  },
  info: {
    bg: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    icon: HiInformationCircle,
    iconColor: 'text-indigo-600',
  },
};

function normalizeNotifyArgs(message, typeOrOptions) {
  if (typeof typeOrOptions === 'object' && typeOrOptions !== null) {
    return {
      message: typeOrOptions.message ?? message,
      type: typeOrOptions.type || 'info',
      duration: typeOrOptions.duration ?? 5000,
    };
  }
  return {
    message,
    type: typeof typeOrOptions === 'string' ? typeOrOptions : 'info',
    duration: 5000,
  };
}

function normalizeConfirmOptions(messageOrOptions) {
  if (typeof messageOrOptions === 'string') {
    return { message: messageOrOptions };
  }
  return messageOrOptions || {};
}

export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null);
  const confirmResolverRef = useRef(null);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const notify = useCallback((message, typeOrOptions = 'info') => {
    const { message: text, type, duration } = normalizeNotifyArgs(message, typeOrOptions);
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message: text, type }]);
    if (duration > 0) {
      setTimeout(() => dismissToast(id), duration);
    }
    return id;
  }, [dismissToast]);

  const confirm = useCallback((messageOrOptions) => {
    const options = normalizeConfirmOptions(messageOrOptions);
    return new Promise((resolve) => {
      confirmResolverRef.current = resolve;
      setConfirmState({
        title: options.title || 'Подтверждение',
        message: options.message || '',
        confirmText: options.confirmText || 'Подтвердить',
        cancelText: options.cancelText || 'Отмена',
        danger: options.danger ?? false,
      });
    });
  }, []);

  const closeConfirm = useCallback((result) => {
    setConfirmState(null);
    if (confirmResolverRef.current) {
      confirmResolverRef.current(result);
      confirmResolverRef.current = null;
    }
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, confirm }}>
      {children}

      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-full pointer-events-none px-4 sm:px-0">
        {toasts.map((toast) => {
          const styles = TYPE_STYLES[toast.type] || TYPE_STYLES.info;
          const Icon = styles.icon;
          return (
            <div
              key={toast.id}
              role="status"
              className={`pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg backdrop-blur-sm ${styles.bg}`}
            >
              <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${styles.iconColor}`} />
              <p className="text-sm flex-1 whitespace-pre-wrap">{toast.message}</p>
              <button
                type="button"
                onClick={() => dismissToast(toast.id)}
                className="shrink-0 opacity-60 hover:opacity-100"
                aria-label="Закрыть"
              >
                <HiX className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>

      {confirmState && (
        <ModalOverlay zIndex={110} onClick={() => closeConfirm(false)}>
          <div
            className="modal-panel rounded-2xl p-6 max-w-md w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{confirmState.title}</h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap mb-6">{confirmState.message}</p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => closeConfirm(false)}
                className="glass-btn-secondary px-4 py-2 text-sm"
              >
                {confirmState.cancelText}
              </button>
              <button
                type="button"
                onClick={() => closeConfirm(true)}
                className={
                  confirmState.danger
                    ? 'px-4 py-2 text-sm rounded-xl bg-red-600 text-white hover:bg-red-700'
                    : 'glass-btn px-4 py-2 text-sm'
                }
              >
                {confirmState.confirmText}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return ctx;
}
